/**
 * Enums do domínio de lead — espelham `prisma/schema.prisma` §ENUMS e
 * `docs/spec/02-domains.md §11`.
 *
 * RNF-121: declarados UMA vez aqui e consumidos pela API (validação de entrada) e
 * pela web (seletores). Divergência entre este arquivo e o schema Prisma é erro
 * de build — o teste `leads-transicoes.spec.ts` compara os dois conjuntos.
 */
import { z } from 'zod'

export const STATUS_LEAD = [
  'EM_ABERTO',
  'EM_ATENDIMENTO',
  'AGENDADO',
  'PROPOSTA',
  'VENDA_REALIZADA',
  'VENDA_NAO_REALIZADA',
  'FINALIZADO',
] as const

export type StatusLead = (typeof STATUS_LEAD)[number]

/**
 * Status terminais (`03-flows.md §1`). Terminal só sai por
 * `POST /leads/{uuid}/reabrir`, exclusivo de Gestor+ (RN-20, última linha).
 */
export const STATUS_TERMINAIS: ReadonlyArray<StatusLead> = [
  'VENDA_REALIZADA',
  'VENDA_NAO_REALIZADA',
  'FINALIZADO',
]

/** Status ativos — os que aparecem no painel e nas filas de trabalho. */
export const STATUS_ATIVOS: ReadonlyArray<StatusLead> = [
  'EM_ABERTO',
  'EM_ATENDIMENTO',
  'AGENDADO',
  'PROPOSTA',
]

export function ehStatusTerminal(status: StatusLead): boolean {
  return STATUS_TERMINAIS.includes(status)
}

export const PERCEPCOES = [
  'NAO_IDENTIFICADO',
  'POTENCIAL_PEQUENO',
  'POTENCIAL_MEDIO',
  'POTENCIAL_GRANDE',
] as const

export type Percepcao = (typeof PERCEPCOES)[number]

/** Derivada pelo sistema (RN-21 / ⚠️ S6). **Nunca** aceita do cliente HTTP. */
export const TEMPERATURAS = ['FRIO', 'QUENTE', 'ABORDAGEM', 'EM_CONTATO'] as const

export type Temperatura = (typeof TEMPERATURAS)[number]

export const TIPOS_INTERACAO = [
  'CRIACAO',
  'ATENDIMENTO_MANUAL',
  'MUDANCA_STATUS',
  'TRANSFERENCIA',
  'AGENDAMENTO_CRIADO',
  'AGENDAMENTO_CONCLUIDO',
  'AGENDAMENTO_CANCELADO',
  'PROPOSTA_CRIADA',
  'IMPORTACAO',
  'INTEGRACAO_RECEBIDA',
  'WHATSAPP_ABERTO',
  'FINALIZACAO_AUTOMATICA',
  'AVALIACAO_RECEBIDA',
] as const

export type TipoInteracao = (typeof TIPOS_INTERACAO)[number]

/**
 * Tipos gerados por integração externa. `payloadExterno` só existe nestes, e é o
 * recorte que `INTERACAO_LER_PAYLOAD_INTEGRACAO` protege (§6.4).
 */
export const TIPOS_INTERACAO_EXTERNA: ReadonlyArray<TipoInteracao> = [
  'INTEGRACAO_RECEBIDA',
  'IMPORTACAO',
]

/**
 * Tipos que representam **atendimento humano** — a interação que preenche
 * `leads.primeira_interacao_em` (RN-20 / RN-22).
 *
 * É a definição de "o corretor começou a atender este lead". Vive aqui, e não no
 * serviço que escreve a timeline, porque agora tem **dois** consumidores: a escrita
 * (`InteracoesService.sincronizarLead`, que decide se a interação zera o SLA) e a
 * leitura (o ranking de velocidade de atendimento da TELA-01, que precisa achar
 * exatamente a mesma interação para saber quem atendeu e quando). Duas listas
 * separadas divergiriam no primeiro tipo novo de interação, e o ranking passaria a
 * medir coisa diferente do bloco "Aguardando" sem ninguém perceber.
 *
 * O que está **de fora** é decisão de negócio, documentada em `InteracoesService`:
 *
 *  - `CRIACAO`, `INTEGRACAO_RECEBIDA`, `IMPORTACAO` — descrevem de onde o lead
 *    veio; o cliente ainda não foi acionado. Contá-las zeraria o bloco "Aguardando";
 *  - `TRANSFERENCIA` — transferir não é atender;
 *  - `WHATSAPP_ABERTO` — abrir a conversa não prova que houve contato;
 *  - `FINALIZACAO_AUTOMATICA`, `AVALIACAO_RECEBIDA` — não têm autor humano.
 */
export const TIPOS_INTERACAO_ATENDIMENTO: ReadonlyArray<TipoInteracao> = [
  'ATENDIMENTO_MANUAL',
  'MUDANCA_STATUS',
  'AGENDAMENTO_CRIADO',
  'AGENDAMENTO_CONCLUIDO',
  'AGENDAMENTO_CANCELADO',
  'PROPOSTA_CRIADA',
]

/**
 * `leads.origem_tipo` — por qual porta o lead entrou no CRM.
 *
 * `LAIS` entrou na 25ª entrega. Não é `API` nem `PORTAL`, e a distinção é
 * operacional: o lead da Lais chega **já qualificado por conversa**, com resumo e
 * histórico de atendimento, e o corretor precisa saber disso antes de ligar. Dobrado
 * em `API`, o filtro "leads da Lais" da TELA-02 não existiria e o relatório de
 * eficiência da qualificação não teria recorte.
 *
 * A origem *dentro* da Lais (`GrupoZapLais`, `FacebookAdsLais`, …) é outra coisa e
 * mora preservada em `lais_leads.origem` — ver a nota de `LaisLead` no schema.
 */
export const ORIGENS_LEAD = [
  'MANUAL',
  'META_LEADS',
  'IMPORTACAO',
  'PORTAL',
  'API',
  'LAIS',
] as const

export type OrigemLead = (typeof ORIGENS_LEAD)[number]

export const STATUS_AGENDAMENTO = [
  'AGENDADO',
  'CONCLUIDO',
  'CANCELADO',
  'NAO_COMPARECEU',
  'REAGENDADO',
] as const

export type StatusAgendamento = (typeof STATUS_AGENDAMENTO)[number]

/** Agendamento que ainda prende o lead na fila de retorno (RN-25A). */
export function agendamentoEstaAtivo(status: StatusAgendamento): boolean {
  return status === 'AGENDADO'
}

export const TIPOS_DOCUMENTO = ['CPF', 'CNPJ'] as const

export type TipoDocumento = (typeof TIPOS_DOCUMENTO)[number]

// --- Schemas Zod ------------------------------------------------------------
// Declarados junto dos valores para que não exista a chance de um schema aceitar
// um valor que o enum do banco recusaria.

export const zStatusLead = z.enum(STATUS_LEAD)
export const zPercepcao = z.enum(PERCEPCOES)
export const zTemperatura = z.enum(TEMPERATURAS)
export const zTipoInteracao = z.enum(TIPOS_INTERACAO)
export const zOrigemLead = z.enum(ORIGENS_LEAD)
export const zStatusAgendamento = z.enum(STATUS_AGENDAMENTO)
export const zTipoDocumento = z.enum(TIPOS_DOCUMENTO)

// --- Rótulos pt-BR ----------------------------------------------------------
// Ficam aqui, e não no `apps/web`, porque relatório exportado (CSV) é montado no
// servidor e precisa do mesmo texto que a tela mostra (RNF-111).

export const ROTULO_STATUS_LEAD: Readonly<Record<StatusLead, string>> = {
  EM_ABERTO: 'Em aberto',
  EM_ATENDIMENTO: 'Em atendimento',
  AGENDADO: 'Agendado',
  PROPOSTA: 'Proposta',
  VENDA_REALIZADA: 'Venda realizada',
  VENDA_NAO_REALIZADA: 'Venda não realizada',
  FINALIZADO: 'Finalizado',
}

export const ROTULO_PERCEPCAO: Readonly<Record<Percepcao, string>> = {
  NAO_IDENTIFICADO: 'Não identificado',
  POTENCIAL_PEQUENO: 'Potencial pequeno',
  POTENCIAL_MEDIO: 'Potencial médio',
  POTENCIAL_GRANDE: 'Potencial grande',
}

export const ROTULO_TEMPERATURA: Readonly<Record<Temperatura, string>> = {
  FRIO: 'Frios',
  QUENTE: 'Quentes',
  ABORDAGEM: 'Abordagens',
  EM_CONTATO: 'Em contato',
}
